import React, { useEffect, useMemo, useState } from 'react';
import * as Ably from 'ably';
import LogoIconSrc from '../../assets/icons/logo/pixle-logo.webp';
import { Link } from 'lucide-react';
import { useParams } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { debounce } from 'lodash';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Menu, Button, TextField } from '@mui/material';
import './header.css';
import { getAvatarColor } from '../commentsmenu/avatarColors.ts';
import { User, videoService } from '../../services/videoService.ts';
import { useNavigate } from 'react-router-dom';
import { Home } from '@mui/icons-material';
import { IconButton } from '@mui/material';

interface HeaderProps {
  onPublish?: () => void;
  /** True while a publish/stitch job is running: button disables itself. */
  isPublishWorking?: boolean;
  /** False until the canvas + timeline engine exist: button waits. */
  isEditorReady?: boolean;
  title?: string | undefined;
  setTitle: React.Dispatch<React.SetStateAction<string | undefined>>;
  channel: Ably.RealtimeChannel;
  ownedBy?: User | undefined;
  sharedWith: User[];
  setSharedWith: React.Dispatch<React.SetStateAction<User[]>>;
}

const Header: React.FC<HeaderProps> = ({
  onPublish,
  isPublishWorking,
  isEditorReady = true,
  title,
  setTitle,
  channel,
  ownedBy,
  sharedWith,
  setSharedWith,
}) => {
  const navigate = useNavigate();
  const { id: videoId } = useParams();
  const [inviteLoading, setInviteLoading] = useState(false);
  const [collaborators, setCollaborators] = useState<
    { id: string; name: string; initials: string; color: string }[]
  >([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [inviteEmail, setInviteEmail] = useState('');

  // Stable debounced title PATCH. Must be memoized: an inline debounce(...)
  // would mint a fresh timer on every keystroke-render, firing one PATCH
  // per keystroke instead of a single trailing call.
  const debouncedUpdate = useMemo(
    () =>
      debounce((id: string, title: string) => {
        videoService.patchVideo(id, { title });
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  const handleFileNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
    channel.publish('handleFileNameUpdate', event.target.value);

    if (videoId) {
      debouncedUpdate(videoId, event.target.value);
    }
  };

  const handleInviteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const fetchSharedWith = async () => {
    try {
      if (videoId) {
        const video = await videoService.getVideoById(videoId); // Ensure this API returns sharedWith list
        setSharedWith((video.sharedWith as User[]) || []);
      }
    } catch (error) {
      console.error('Error fetching sharedWith list:', error);
    }
  };

  const handleInviteClose = () => {
    setAnchorEl(null);
  };

  const handleSendInvite = async () => {
    if (inviteEmail && videoId) {
      console.log(`Sending invite to: ${inviteEmail}`);
      setInviteLoading(true);
      try {
        await videoService.shareVideo(videoId, inviteEmail); // Fixed function call
      } catch (error) {
        console.error('Error sharing video:', error);
      } finally {
        setInviteLoading(false); // Stop loading state
      }
      setInviteEmail('');
      handleInviteClose();
    }
  };

  const handleUnshare = async (videoId: string | undefined, email: string) => {
    if (!videoId) return;

    try {
      await videoService.unshareVideo(videoId, email);
      setSharedWith(sharedWith.filter((user) => user.email !== email)); // Remove user from state
    } catch (error) {
      console.error('Error unsharing video:', error);
    }
  };

  useEffect(() => {
    if (anchorEl) {
      fetchSharedWith();
    }
  }, [anchorEl]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userName = user.firstName + ' ' + user.lastName;
    const userId = user._id || user.id || localStorage.getItem('userId') || '';

    const updateCollaborators = async () => {
      const presenceSet = await channel.presence.get();
      const updatedCollaborators = presenceSet.map((presence) => {
        const fullName: string = presence.data.userName;
        const initials = fullName
          .split(' ')
          .map((word) => word[0])
          .join('')
          .toUpperCase();

        return {
          id: presence.data.userId,
          initials: initials,
          name: fullName,
          // Stable per-person color (same as comments), not join order.
          color: getAvatarColor(fullName),
        };
      });
      setCollaborators(updatedCollaborators);
    };

    channel.presence.subscribe('enter', updateCollaborators);
    channel.presence.subscribe('leave', updateCollaborators);

    const joinPresence = async () => {
      await channel.presence.enter({ userName: userName, userId: userId });
      updateCollaborators();
    };

    joinPresence();

    return () => {
      const leavePresence = async () => {
        await channel.presence.leave();
      };
      leavePresence();
      channel.presence.unsubscribe('enter', updateCollaborators);
      channel.presence.unsubscribe('leave', updateCollaborators);
    };
  }, [channel]);

  const CopyLinkButton: React.FC<{ link: string }> = ({ link }) => {
    const [linkCopied, setLinkCopied] = useState(false);
    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(link);
        setLinkCopied(true);
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
    };

    return (
      <div className="copy-link-container">
        <Button className="copy-link-btn" onClick={handleCopy}>
          {linkCopied ? (
            'Link copied!'
          ) : (
            <>
              <Link className="icon" size={18} /> Copy link
            </>
          )}
        </Button>
      </div>
    );
  };

  const getInitials = (name: string) => {
    const names = name.split(' ');
    return names
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <header className="header">
      <div className="main-bar">
        {/*<button className="menu-btn">
          <i className="fas fa-bars"></i>
        </button>*/}
        <IconButton
          className="home-nav-button"
          onClick={() => navigate('/')}
          aria-label="Home"
        >
          <Home />
        </IconButton>
        <img
          src={LogoIconSrc}
          className="header-logo"
          alt="Pixle"
          width="150"
          height="44"
        />

        <div className="partition"></div>
       
        <input
          type="text"
          className="file-name"
          value={title}
          onChange={handleFileNameChange}
        />
        <div className="spacer"></div>
        <div className="collaborators">
          {collaborators.map((collab, index) => (
            <span
              key={index}
              className="avatar"
              style={{ backgroundColor: collab.color }}
              title={collab.name}
            >
              {collab.initials}
            </span>
          ))}

          <Button
            onClick={handleInviteClick}
            className="avatar add-collaborator"
          >
            <AddIcon />
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleInviteClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right', // Aligns the menu to the right of the button
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right', // Ensures it expands from the right
            }}
            className="share-modal"
          >
            <div style={{ padding: '10px', width: '320px' }}>
              <CopyLinkButton link={window.location.href} />
              <TextField
                fullWidth
                className="invite-textbox"
                size="small"
                label="Invite via email"
                variant="outlined"
                value={inviteEmail}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent form submission
                    handleSendInvite(); // Call the invite function
                  }
                }}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <Button
                fullWidth
                variant="contained"
                className="invite-button"
                onClick={handleSendInvite}
                style={{ marginTop: '10px' }}
              >
                {inviteLoading ? 'Sending...' : 'Invite'}
              </Button>
            </div>
            <div className="access-list">
              <h3>Who has access</h3>

              {/* Owner */}
              {ownedBy && (
                <div className="collaborators-list">
                  <span
                    className="avatar"
                    style={{
                      backgroundColor: getAvatarColor(
                        `${ownedBy.firstName} ${ownedBy.lastName}`
                      ),
                    }}
                    title={ownedBy.email} // Show email on hover
                  >
                    {getInitials(`${ownedBy.firstName} ${ownedBy.lastName}`)}
                  </span>
                  <span>
                    {ownedBy.firstName} {ownedBy.lastName}
                  </span>
                  <span className="permission">owner</span>
                </div>
              )}

              {/* Shared Users */}
              {sharedWith.map((collab) => (
                <div key={collab._id} className="collaborators-list">
                  <span
                    className="avatar"
                    style={{
                      backgroundColor: getAvatarColor(
                        `${collab.firstName} ${collab.lastName}`
                      ),
                    }}
                    title={collab.email} // Show email on hover
                  >
                    {getInitials(`${collab.firstName} ${collab.lastName}`)}
                  </span>
                  <span>
                    {collab.firstName} {collab.lastName}
                  </span>
                  <span className="permission">can edit</span>

                  {/* Show "Cancel" only if current user is the owner */}
                  {ownedBy &&
                    ownedBy._id ===
                      JSON.parse(localStorage.getItem('user') || '{}').id && (
                      <button
                        className="unshare-btn"
                        onClick={() => handleUnshare(videoId, collab.email)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#999',
                          marginLeft: '10px',
                        }}
                        title="Remove access"
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                    )}
                </div>
              ))}
            </div>
          </Menu>
        </div>
        <button
          className="publish-btn"
          onClick={onPublish}
          disabled={isPublishWorking || !isEditorReady}
          title={
            isPublishWorking
              ? 'Publish in progress…'
              : !isEditorReady
                ? 'Loading editor…'
                : 'Publish'
          }
        >
          {isPublishWorking ? 'Publishing…' : 'Publish'}{' '}
          <i className="fas fa-upload icon-spacing"></i>
        </button>
      </div>
    </header>
  );
};

export default Header;
